import React, { useState, useEffect, useCallback } from "react";
import { ChevronLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { CURRENCY_RATES, getCurrencyForDestination } from "../data/currencyRates";
import { toast } from "sonner";
import { BudgetSummaryCard } from "../components/budget/BudgetSummaryCard";
import { BudgetCalculator } from "../components/budget/BudgetCalculator";
import { BudgetDeductAction } from "../components/budget/BudgetDeductAction";
import { BudgetHistory } from "../components/budget/BudgetHistory";
import { BudgetSetupModal } from "../components/budget/BudgetSetupModal";

const LORA = { fontFamily: "'Lora', Georgia, serif" };
const SERIF = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const LS_BUDGET = "voyage_r_budget";
const LS_TRIP   = "voyage_r_active_trip";

const INITIAL = { total: null, remaining: null, currency: "EUR", history: [] };

function safeEval(expr) {
  const s = expr.replace(/×/g, "*").replace(/÷/g, "/");
  if (!s || !/^[\d\s+\-*/().]+$/.test(s)) return null;
  try {
    // eslint-disable-next-line no-new-func
    const r = Function('"use strict"; return (' + s + ')')();
    return typeof r === "number" && isFinite(r) ? Math.round(r * 1e10) / 1e10 : null;
  } catch { return null; }
}

function fmt(n, d = 2) {
  return Number(n).toLocaleString("fr-FR", { minimumFractionDigits: d, maximumFractionDigits: d });
}

function colors(pct) {
  if (pct > 50) return { bg: "#F0FDF4", text: "#16A34A", bar: "#4ADE80", border: "#BBF7D0", label: "Bonne santé" };
  if (pct > 15) return { bg: "#FFF7ED", text: "#EA580C", bar: "#FB923C", border: "#FED7AA", label: "Attention" };
  return           { bg: "#FEF2F2", text: "#DC2626", bar: "#F87171", border: "#FECACA", label: "Budget critique" };
}

export default function BudgetPage() {
  const navigate = useNavigate();

  const [state, setState] = useState(() => {
    try {
      const s = localStorage.getItem(LS_BUDGET);
      return s ? { ...INITIAL, ...JSON.parse(s) } : INITIAL;
    } catch { return INITIAL; }
  });

  const [expr, setExpr]             = useState("");
  const [hasResult, setHasResult]   = useState(false);
  const [isLocal, setIsLocal]       = useState(true);
  const [showSetup, setShowSetup]   = useState(false);
  const [setupVal, setSetupVal]     = useState("");
  const [labelInput, setLabelInput] = useState("");

  // Auto-detect currency from active trip
  useEffect(() => {
    if (state.currency !== "EUR") return;
    try {
      const saved = JSON.parse(localStorage.getItem(LS_TRIP) || "null");
      if (saved?.destination) {
        const c = getCurrencyForDestination(saved.destination);
        if (c && c !== "EUR") setState(s => ({ ...s, currency: c }));
      }
    } catch (e) { console.warn("[BudgetPage] localStorage read failed:", e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show setup on first visit
  useEffect(() => {
    if (!state.total) setShowSetup(true);
  }, [state.total]);

  // Persist
  useEffect(() => {
    try { localStorage.setItem(LS_BUDGET, JSON.stringify(state)); } catch (e) { console.warn("[BudgetPage] localStorage write failed:", e); }
  }, [state]);

  const info = CURRENCY_RATES[state.currency] || CURRENCY_RATES.EUR;
  const currSymbol = isLocal ? info.symbol : "€";
  const curVal = safeEval(expr) ?? (parseFloat(expr) || 0);
  const eurVal = isLocal && state.currency !== "EUR" ? curVal / info.rate : curVal;
  const pct = state.total > 0 ? Math.max(0, (state.remaining / state.total) * 100) : 100;
  const c = colors(pct);

  const handleCalc = useCallback((key) => {
    if (key === "C") { setExpr(""); setHasResult(false); return; }
    if (key === "⌫") { setExpr(e => e.slice(0, -1)); setHasResult(false); return; }
    if (key === "=") {
      const r = safeEval(expr);
      if (r !== null) { setExpr(Number.isInteger(r) ? String(r) : r.toFixed(2)); setHasResult(true); }
      return;
    }
    const ops = ["+", "-", "×", "÷"];
    if (hasResult && !ops.includes(key)) setExpr(key);
    else setExpr(e => e + key);
    setHasResult(false);
  }, [expr, hasResult]);

  const handleDeduct = () => {
    const eur = Math.round(eurVal * 100) / 100;
    if (!state.total) { toast.error("Définissez d'abord votre budget"); setShowSetup(true); return; }
    if (!eur || eur <= 0) { toast.error("Entrez un montant valide"); return; }
    const entry = {
      id: Date.now(),
      amountEur: eur,
      originalAmount: curVal,
      originalCurrency: isLocal ? state.currency : "EUR",
      label: labelInput.trim() || new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }),
      date: new Date().toISOString(),
    };
    setState(s => ({ ...s, remaining: Math.max(0, s.remaining - eur), history: [entry, ...s.history].slice(0, 20) }));
    setExpr(""); setLabelInput(""); setHasResult(false);
    toast.success(`-${fmt(eur)} € déduit`);
  };

  const handleUndo = (id) => {
    const entry = state.history.find(h => h.id === id);
    if (!entry) return;
    setState(s => ({ ...s, remaining: Math.min(s.total, s.remaining + entry.amountEur), history: s.history.filter(h => h.id !== id) }));
    toast.success("Dépense annulée");
  };

  const handleSetBudget = () => {
    const val = parseFloat(setupVal);
    if (!val || val <= 0) return;
    const isNew = !state.total;
    setState(s => ({ ...s, total: val, remaining: isNew ? val : s.remaining }));
    setSetupVal(""); setShowSetup(false);
    toast.success(isNew ? "Budget défini !" : "Budget mis à jour !");
  };

  const handleReset = () => {
    if (!window.confirm("Réinitialiser le budget et l'historique ?")) return;
    setState(INITIAL);
  };

  return (
    <div className="min-h-screen" style={{ background: "#F9F6F0" }}>
      <Navbar />

      <div className="max-w-md mx-auto px-4 pt-6 pb-16">

        {/* Page header */}
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate(-1)} className="text-[#9A8F86] hover:text-[#2B2623] transition-colors" data-testid="btn-back">
            <ChevronLeft size={20} strokeWidth={1.5} />
          </button>
          <div>
            <h1 className="text-3xl" style={{ ...SERIF, fontWeight: 600, color: "#2B2623" }}>Suivi de budget</h1>
            <p className="text-xs text-[#9A8F86] italic mt-0.5" style={LORA}>Fonctionne hors-ligne · Données locales</p>
          </div>
        </div>

        <BudgetSummaryCard
          state={state}
          c={c}
          pct={pct}
          info={info}
          onModify={() => { setSetupVal(state.total ? String(state.total) : ""); setShowSetup(true); }}
          onReset={handleReset}
        />

        <BudgetCalculator
          expr={expr}
          currSymbol={currSymbol}
          isLocal={isLocal}
          currency={state.currency}
          info={info}
          curVal={curVal}
          eurVal={eurVal}
          onCalc={handleCalc}
          onToggleCurrency={() => setIsLocal(l => !l)}
        />

        <BudgetDeductAction
          curVal={curVal}
          currSymbol={currSymbol}
          isLocal={isLocal}
          currency={state.currency}
          eurVal={eurVal}
          labelInput={labelInput}
          onLabelChange={setLabelInput}
          onDeduct={handleDeduct}
        />

        <BudgetHistory history={state.history} onUndo={handleUndo} />
      </div>

      <BudgetSetupModal
        show={showSetup}
        total={state.total}
        setupVal={setupVal}
        currency={state.currency}
        onSetupValChange={setSetupVal}
        onCurrencyChange={(val) => setState(s => ({ ...s, currency: val }))}
        onConfirm={handleSetBudget}
        onClose={() => setShowSetup(false)}
      />
    </div>
  );
}
